import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ArrowLeft, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

import { BACKEND_URL } from '../config';

const LatestNewsPage = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/news?published_only=true&limit=100`);
      setNews(response.data);
    } catch (error) {
      console.error('Failed to fetch news:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar scrolled={scrolled} />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0d6571] to-[#0d6571]/80 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-white mb-4">Latest News</h1>
          <p className="text-white/90 text-lg">Stay updated with our recent announcements and insights</p>
        </div>
      </div>

      {/* News Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0d6571]"></div>
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow">
            <p className="text-gray-500 mb-4">No news articles available yet.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {news.map((article) => (
              <div
                key={article.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
              >
                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center text-gray-500 text-sm mb-3">
                        <Calendar className="w-4 h-4 mr-2" />
                        {formatDate(article.createdAt)}
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        {article.title}
                      </h2>
                      <p className="text-gray-600 text-lg leading-relaxed mb-4">
                        {article.description}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedNews(article)}
                    className="inline-flex items-center px-6 py-3 bg-[#0d6571] hover:bg-[#0d6571]/90 text-white font-semibold rounded-lg transition-colors"
                  >
                    Read Full Article
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* News Modal */}
      {selectedNews && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-8 py-6 flex items-center justify-between">
              <h2 className="text-3xl font-bold text-gray-900">{selectedNews.title}</h2>
              <button
                onClick={() => setSelectedNews(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-8">
              <div className="flex items-center text-gray-500 text-sm mb-6">
                <Calendar className="w-4 h-4 mr-2" />
                {formatDate(selectedNews.createdAt)}
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 whitespace-pre-line leading-relaxed text-lg">
                  {selectedNews.fullContent}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default LatestNewsPage;
